import SolicitacaoVisita from "@/models/SolicitacaoVisita";
import { normalizarVisitanteDocumentos, visitanteParaPayloadApi } from "@/config/visitante-documentos";
import { api } from "roboflex-thalamus-request-handler";
import { sso } from "roboflex-thalamus-sso-lib";
import { expandirItemAgendaPorDias, visitaIncluiDia, ehVisitaPorPeriodo as ehVisitaPorPeriodoFn } from "@/utils/visita-data";

const BASE = "solicitacao-visita";

function idUsuarioLogado() {
  const u = sso.getUsuarioLogado?.();
  const id = Number(u?.id ?? 0);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function horaLocalDeIso(iso) {
  if (!iso || typeof iso !== "string" || !String(iso).trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Garante string HH:MM ou "" (evita quebra se a API mandar Date/object). */
function asHhMmOuVazio(h) {
  if (h == null || h === "") return "";
  const s = String(h).trim();
  if (s.length >= 5) return s.slice(0, 5);
  return s;
}

function normalizarItemAgenda(item) {
  const id = item.id ?? item._id;
  const visitanteIndex = item.visitante_index != null ? Number(item.visitante_index) : 0;
  const agendaItemKey = item.agenda_item_key ?? `${id}_${visitanteIndex}`;
  const lista = item.listaVisitantes;
  const vi =
    Array.isArray(lista) && lista.length > 0 && Number.isFinite(visitanteIndex) && visitanteIndex >= 0
      ? lista[visitanteIndex]
      : null;
  const nomeLinha = vi ? String(vi.nomeCompleto ?? vi.nome_completo ?? "").trim() : "";
  const documentoVisitante = vi
    ? (() => {
        const docs = normalizarVisitanteDocumentos(vi);
        return [docs.cpf, docs.rg].filter(Boolean).join(" · ");
      })()
    : "";
  const nomeFallback =
    item.nomeCompleto ??
    item.nome_completo ??
    item.nome ??
    (Array.isArray(lista) && lista.length > 1
      ? lista.map((v) => v.nomeCompleto || v.nome_completo).filter(Boolean).join(", ")
      : "—");
  const nome = nomeLinha || nomeFallback;
  const empresa = item.empresaOrigem ?? item.empresa_origem ?? item.empresa ?? "—";
  const dataVisita = item.dataVisita ?? item.data_visita;
  const dataVisitaFim = item.dataVisitaFim ?? item.data_visita_fim ?? null;
  const ehVisitaPorPeriodo =
    Boolean(item.ehVisitaPorPeriodo ?? item.eh_visita_por_periodo) ||
    ehVisitaPorPeriodoFn(dataVisita, dataVisitaFim);
  const previstoEnt = item.horarioEntradaPrevisto ?? item.horario_entrada_previsto ?? item.horario_entrada ?? "";
  const previstoSai = item.horarioSaidaPrevisto ?? item.horario_saida_previsto ?? item.horario_saida ?? "";
  const checkinIso = item.checkinEm ?? item.checkin_em;
  const checkoutIso = item.checkoutEm ?? item.checkout_em;
  const status = Number(item.status ?? item.status_visita ?? item.statusAgendamento ?? 1);
  const entradaEfetiva = status >= 5 ? horaLocalDeIso(checkinIso) : "";
  const saidaEfetiva = status >= 6 ? horaLocalDeIso(checkoutIso) : "";
  const horarioEntrada = asHhMmOuVazio(entradaEfetiva || previstoEnt);
  const horarioSaida = asHhMmOuVazio(saidaEfetiva || previstoSai);
  return {
    id,
    visitanteIndex,
    agendaItemKey,
    totalVisitantesGrupo: item.total_visitantes_grupo != null ? Number(item.total_visitantes_grupo) : null,
    documentoVisitante,
    nome,
    empresa,
    dataVisita,
    dataVisitaFim,
    ehVisitaPorPeriodo,
    horarioEntrada,
    horarioSaida,
    status,
    cancelada: Boolean(item.cancelada),
    motivoVisita: item.motivoVisita ?? item.motivo_visita ?? "",
    areaSetorVisitado: item.areaSetorVisitado ?? item.area_setor_visitado ?? "",
    nomeAnfitriao: String(item.nomeAnfitriao ?? item.nome_anfitriao ?? "").trim(),
  };
}

function horarioParaChave(h) {
  const s = asHhMmOuVazio(h);
  return s || "00:00";
}

function ordenarPorHorario(a, b) {
  return horarioParaChave(a.horarioEntrada).localeCompare(horarioParaChave(b.horarioEntrada));
}

function listar(params = {}) {
  const page = params.page ?? 1;
  const search = params.search ? `&search=${encodeURIComponent(params.search)}` : "";
  const origem = params.origem ? `&origem=${encodeURIComponent(params.origem)}` : "";
  const minhas = params.minhas ? "&minhas=1" : "";
  const usuarioLogadoId = idUsuarioLogado();
  const usuarioLogadoQuery = params.minhas && usuarioLogadoId ? `&usuario_logado_id=${usuarioLogadoId}` : "";
  return api.get(`${BASE}?page=${page}${search}${origem}${minhas}${usuarioLogadoQuery}`).then((res) => ({
    data: (res.data?.data ?? res.data ?? []).map((item) => SolicitacaoVisita.fromApi(item)),
    current_page: res.data?.current_page ?? 1,
    last_page: res.data?.last_page ?? 1,
    total: res.data?.total ?? 0,
  }));
}

function obterPorId(id) {
  return api.get(`${BASE}/${id}`).then((res) => SolicitacaoVisita.fromApi(res.data));
}

function criar(solicitacao) {
  const payload = solicitacao instanceof SolicitacaoVisita ? solicitacao.toPayload() : solicitacao;
  const usuarioLogadoId = idUsuarioLogado();
  if (usuarioLogadoId) {
    payload.usuario_logado_id = usuarioLogadoId;
    if (!payload.criado_por_usuario_id) payload.criado_por_usuario_id = usuarioLogadoId;
  }
  return api.post(BASE, payload).then((res) => {
    const id = res.data?.id ?? res.data?.data?.id ?? res.id;
    return id ? obterPorId(id) : res.data;
  });
}

function atualizar(id, solicitacao) {
  const payload = solicitacao instanceof SolicitacaoVisita ? solicitacao.toPayload() : solicitacao;
  return api.put(`${BASE}/${id}`, payload).then((res) => (res.data ? SolicitacaoVisita.fromApi(res.data) : res.data));
}

/** PUT parcial: só `empresa_origem` (correção na recepção / check list). */
function atualizarEmpresaOrigem(id, empresaOrigem) {
  const v = empresaOrigem != null ? String(empresaOrigem).trim() : "";
  return api.put(`${BASE}/${id}`, { empresa_origem: v }).then((res) => (res.data ? SolicitacaoVisita.fromApi(res.data) : res.data));
}

/** PUT parcial: só `visitantes` (correção na recepção). Cada item: nomeCompleto, cpf, rg, email, telefoneWhatsapp, visitanteId opcional. */
function atualizarVisitantes(id, visitantesLista) {
  const visitantes = (Array.isArray(visitantesLista) ? visitantesLista : []).map((v) => {
    const row = {
      nome_completo: String(v.nomeCompleto ?? "").trim(),
      ...visitanteParaPayloadApi(v),
      email: String(v.email ?? "").trim(),
      telefone_whatsapp: String(v.telefoneWhatsapp ?? "").trim(),
    };
    const vid = v.visitanteId ?? v.visitante_id;
    if (vid != null && Number(vid) > 0) {
      row.visitante_id = Number(vid);
    }
    const fb = String(v.fotoBase64 ?? v.foto_base64 ?? "").trim();
    if (fb) {
      row.foto_base64 = fb;
    }
    return row;
  });
  return api.put(`${BASE}/${id}`, { visitantes }).then((res) => (res.data ? SolicitacaoVisita.fromApi(res.data) : res.data));
}

function criarPeloAnfitriao(dados) {
  return api.post(`${BASE}/anfitriao`, dados).then((res) => res.data).catch((err) => {
    if (err?.response?.status === 404 || err?.response?.status === 422) {
      return api.post(BASE, { ...dados, status_agendamento: 1, origem: "anfitriao" }).then((r) => r.data);
    }
    throw err;
  });
}

function atualizarStatus(id, statusAgendamento) {
  return api.patch(`${BASE}/${id}/status`, { status_agendamento: statusAgendamento }).then((res) => res.data);
}

const CAMPOS_FLUXO_CONVITE_CAMEL_TO_SNAKE = {
  conviteEnviadoEm: "convite_enviado_em",
  informacoesPreenchidasEm: "informacoes_preenchidas_em",
  documentoConfidencialidadeRecebidoEm: "documento_confidencialidade_recebido_em",
  confirmacaoPresencaVisitanteEm: "confirmacao_presenca_visitante_em",
  agendamentoSalaEfetuadoEm: "agendamento_sala_efetuado_em",
  necessidadesLogisticasEncaminhadasEm: "necessidades_logisticas_encaminhadas_em",
};

function atualizarFluxoConvite(id, dados) {
  const payload = {};
  const snakesPermitidos = new Set(Object.values(CAMPOS_FLUXO_CONVITE_CAMEL_TO_SNAKE));
  for (const [camel, snake] of Object.entries(CAMPOS_FLUXO_CONVITE_CAMEL_TO_SNAKE)) {
    if (Object.prototype.hasOwnProperty.call(dados, camel)) payload[snake] = dados[camel];
  }
  for (const snake of snakesPermitidos) {
    if (Object.prototype.hasOwnProperty.call(dados, snake)) payload[snake] = dados[snake];
  }
  // PUT costuma passar por proxies/CDNs que bloqueiam PATCH; a API aceita PATCH e PUT na mesma rota.
  return api.put(`${BASE}/${id}/fluxo-convite`, payload).then((res) => (res.data ? SolicitacaoVisita.fromApi(res.data) : res.data));
}

function cancelar(id, motivo) {
  return api.post(`${BASE}/${id}/cancelar`, { motivo_cancelamento: motivo || "" }).then((res) => (res.data ? SolicitacaoVisita.fromApi(res.data) : res.data));
}

function formatarDataLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function listarAgendaDoDia(data) {
  const dataStr =
    typeof data === "string"
      ? String(data).slice(0, 10)
      : data
        ? formatarDataLocalYmd(new Date(data))
        : formatarDataLocalYmd(new Date());
  try {
    const res = await api.get("visita/agenda", { params: { data: dataStr } });
    const raw = res.data?.data ?? res.data ?? [];
    return Array.isArray(raw) ? raw.map(normalizarItemAgenda).sort(ordenarPorHorario) : [];
  } catch (_) {
    const res = await listar({ page: 1 });
    const lista = res.data || [];
    const noDia = lista.filter((s) => {
      const d = s.dataVisita;
      if (!d) return false;
      return visitaIncluiDia(d, s.dataVisitaFim, dataStr);
    });
    return noDia.map(normalizarItemAgenda).sort(ordenarPorHorario);
  }
}

/** YYYY-MM — todas as linhas da agenda do mês (para calendário). */
async function listarAgendaMes(ym) {
  const mesStr = String(ym || "").trim().slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(mesStr)) {
    return [];
  }
  try {
    const res = await api.get("visita/agenda", { params: { mes: mesStr } });
    const raw = res.data?.data ?? res.data ?? [];
    const normalizados = Array.isArray(raw) ? raw.map(normalizarItemAgenda) : [];
    return normalizados.flatMap(expandirItemAgendaPorDias).sort(ordenarPorHorario);
  } catch (_) {
    const [y, mo1] = mesStr.split("-").map((x) => parseInt(x, 10));
    if (!Number.isFinite(y) || !Number.isFinite(mo1)) return [];
    const ultimo = new Date(y, mo1, 0).getDate();
    const promises = [];
    for (let d = 1; d <= ultimo; d++) {
      const ds = `${y}-${String(mo1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      promises.push(listarAgendaDoDia(ds));
    }
    const arrs = await Promise.all(promises);
    const byKey = new Map();
    for (const arr of arrs) {
      for (const item of arr) {
        const norm = normalizarItemAgenda(item);
        const k = `${norm.id}_${norm.visitanteIndex ?? 0}`;
        if (!byKey.has(k)) byKey.set(k, norm);
      }
    }
    return [...byKey.values()].flatMap(expandirItemAgendaPorDias).sort(ordenarPorHorario);
  }
}

async function listarHistoricoPorVisitaId(visitId) {
  try {
    const res = await api.get(`visita/${visitId}/historico`);
    const data = res.data || {};
    if (Array.isArray(data.visitantes)) return data;
    if (data.visitantePrincipal != null && Array.isArray(data.visitas)) {
      return { visitantes: [{ nomeCompleto: data.visitantePrincipal, visitas: data.visitas }] };
    }
    return { visitantes: [] };
  } catch (_) {
    const sol = await obterPorId(visitId);
    const list = await listar({ page: 1 });
    const listaNomes = [];
    if (sol?.nomeCompleto) listaNomes.push(sol.nomeCompleto.trim());
    if (Array.isArray(sol?.visitantes)) {
      sol.visitantes.forEach((v) => {
        const n = (v.nomeCompleto ?? v.nome_completo)?.trim();
        if (n && !listaNomes.find((x) => x.toLowerCase() === n.toLowerCase())) listaNomes.push(n);
      });
    }
    const visitantes = listaNomes.map((nomeExibicao) => {
      const nomeBusca = nomeExibicao.toLowerCase();
      let visitas = (list.data || []).filter((s) => {
        if (s.nomeCompleto && s.nomeCompleto.toLowerCase() === nomeBusca) return true;
        if (Array.isArray(s.visitantes)) {
          return s.visitantes.some((v) => (v.nomeCompleto ?? v.nome_completo)?.toLowerCase() === nomeBusca);
        }
        return false;
      });
      visitas = visitas.sort((a, b) => {
        const da = a.dataVisita ? new Date(a.dataVisita) : new Date(0);
        const db = b.dataVisita ? new Date(b.dataVisita) : new Date(0);
        return db - da;
      });
      return { nomeCompleto: nomeExibicao, visitas };
    });
    return { visitantes };
  }
}

export default {
  listar,
  obterPorId,
  criar,
  atualizar,
  atualizarEmpresaOrigem,
  atualizarVisitantes,
  atualizarStatus,
  cancelar,
  criarPeloAnfitriao,
  listarAgendaDoDia,
  listarAgendaMes,
  listarHistoricoPorVisitaId,
  atualizarFluxoConvite,
};
